/**
 * The one place an HTTP failure becomes the message a user reads.
 *
 * This used to live inside `@/dataProvider/axios`, reachable only from an axios instance. The
 * ~40 raw `fetch` callsites across the service layer therefore each invented their own string —
 * weave alone produced `create_run 429: {"detail":"llm_budget_exceeded"}`, `run_detail 429` and
 * four more variants for the SAME backend rejection, and `getSecurityReportPdf` reported it as
 * "No scan report available". Mapping lives here, transport-agnostic, so axios and `fetch` agree.
 *
 * The case that forced it: `ProxyController.authorize_llm_budget/2` (ncoder-backend) rejects every
 * non-GET/HEAD to a budget-gated slug with `429 {"detail":"llm_budget_exceeded"}`. One gate, one
 * wire shape — so there is no excuse for N different messages downstream of it.
 */

/** Backend detail codes that need a human message instead of the raw wire value. */
export const FRIENDLY_MESSAGE_BY_DETAIL: Readonly<Record<string, string>> = {
  llm_budget_exceeded: "You've reached your AI budget limit for this cycle.",
};

export const FRIENDLY_MESSAGE_BY_STATUS: Readonly<Record<number, string>> = {
  401: 'Your session expired — please sign in again',
  403: "Sorry, you don't have access to this",
  404: "Sorry, we couldn't find that",
  408: 'Request timed out — please try again',
  429: 'Too many requests — please slow down',
};

interface ErrorBodyShape {
  detail?: unknown;
  message?: unknown;
  error?: unknown;
  errors?: unknown;
}

/**
 * The backend's own error code, if the body carries one.
 *
 * `detail` is FastAPI's shape (every proxied Python service, and the gate deliberately matches it);
 * `error`/`errors` are Phoenix's. A FastAPI `detail` can also be an object — `{detail: {message}}`,
 * used by weave's trigger-ingest — so that shape is unwrapped rather than stringified into
 * "[object Object]".
 */
export function detailFromBody(body: unknown): string | null {
  if (!body || typeof body !== 'object') return null;
  const shape = body as ErrorBodyShape;
  const direct = shape.detail ?? shape.message ?? shape.error;
  if (typeof direct === 'string') return direct;
  if (direct && typeof direct === 'object') {
    const nested = (direct as { message?: unknown }).message;
    if (typeof nested === 'string') return nested;
  }
  return null;
}

/**
 * The backend's machine-readable error CODE, when the body carries one separately from its message.
 *
 * `detailFromBody` above answers "what do I show the user", and for an object detail that is
 * `detail.message`. A typed refusal carries BOTH: `{"detail": {"code": "WRONG_LANE", "message":
 * "This conversation has moved…"}}` — and a caller that has to BRANCH on the refusal needs the
 * code, which the message-oriented reader flattens away. MSA's old lane renders every typed error
 * in exactly this shape (`routers/_lane_guard.py::_wrong_lane_detail`,
 * `routers/interactions.py::_http`), so this is the one place it is read.
 *
 * A STRING detail (`{"detail": "llm_budget_exceeded"}` — the budget gate) IS already the code, and
 * keeps being returned as such, so nothing that matches on that value changes.
 */
export function codeFromBody(body: unknown): string | null {
  if (!body || typeof body !== 'object') return null;
  const detail = (body as ErrorBodyShape).detail;
  if (detail && typeof detail === 'object') {
    const code = (detail as { code?: unknown }).code;
    if (typeof code === 'string' && code) return code;
  }
  return detailFromBody(body);
}

/** A changeset-style `{errors: {field: [msg]}}` body's first message. */
function firstFieldError(body: unknown): string | null {
  if (!body || typeof body !== 'object') return null;
  const errors = (body as ErrorBodyShape).errors;
  if (!errors || typeof errors !== 'object') return null;
  const first = Object.values(errors as Record<string, unknown>)[0];
  const message = Array.isArray(first) ? first[0] : first;
  return typeof message === 'string' ? message : null;
}

/**
 * The user-facing message for a failed request.
 *
 * Order matters and matches what `friendlyMessageForAxiosError` has always done: a backend code
 * wins over the status-code default, because "You've reached your AI budget limit" is strictly
 * more useful than "Too many requests". An unrecognised code is passed through as-is rather than
 * replaced with a generic — the backend said something specific, and hiding it helps nobody.
 */
export function friendlyHttpMessage(input: {
  status?: number;
  body?: unknown;
  /** axios' `error.code`, e.g. ECONNABORTED. Ignored for `fetch`. */
  code?: string;
}): string {
  const { status, body, code } = input;

  const detail = detailFromBody(body);
  if (detail) return FRIENDLY_MESSAGE_BY_DETAIL[detail] ?? detail;

  const fieldError = firstFieldError(body);
  if (fieldError) return fieldError;

  if (status === 401) return FRIENDLY_MESSAGE_BY_STATUS[401];
  if (status === 403) return FRIENDLY_MESSAGE_BY_STATUS[403];
  if (status === 404) return FRIENDLY_MESSAGE_BY_STATUS[404];
  if (status === 408 || code === 'ECONNABORTED') return FRIENDLY_MESSAGE_BY_STATUS[408];
  if (status === 429) return FRIENDLY_MESSAGE_BY_STATUS[429];
  if (status && status >= 500) return "The server isn't responding right now — please try again";
  if (!status) return "Sorry, couldn't reach the server";
  return "Sorry, that didn't work — please try again";
}

/**
 * Refetch the usage rollup when the backend says the seat is over budget, so `UsageLimitBanner`
 * shows itself immediately instead of waiting out its 60s staleTime.
 *
 * Fire-and-forget and dynamically imported for the same reason the axios interceptor does it:
 * this module is imported by the service layer, and a static import of the query client would
 * drag React Query into every one of them.
 */
export function notifyBudgetExceeded(detail: string | null): void {
  if (detail !== 'llm_budget_exceeded') return;
  void import('@/dataProvider/reactQuery/context').then(({ getQueryClient }) => {
    getQueryClient().invalidateQueries({ queryKey: ['my-usage'] });
  });
}

/**
 * An HTTP error carrying what the caller needs to branch on, and a `message` safe to put in a
 * toast. `code` is the backend's raw value (`llm_budget_exceeded`) for callers that must match on
 * it; `message` is the human one.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string | null;
  readonly body: unknown;
  /** The ad-hoc string this callsite used to throw, kept for logs. */
  readonly rawMessage: string;

  constructor(status: number, message: string, code: string | null, body: unknown, rawMessage: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.body = body;
    this.rawMessage = rawMessage;
  }
}

/**
 * Turn a non-ok `Response` into an `ApiError`. Replaces
 * `throw new Error(\`create_run ${res.status}: ${await res.text()}\`)` at every fetch callsite.
 *
 * `context` is the operation label those callsites already carried (`create_run`, `refine`). It is
 * kept on `rawMessage` for logs rather than shown, because the user does not know what
 * `create_run` is — but whoever reads the console does.
 */
export async function httpError(res: Response, context: string): Promise<ApiError> {
  const text = await res.text().catch(() => '');
  let body: unknown = null;
  try {
    body = text ? JSON.parse(text) : null;
  } catch {
    body = null; // not JSON (an HTML error page, a proxy's plain-text body) — status drives the message
  }

  // The MESSAGE reader and the CODE reader are deliberately different functions — see
  // `codeFromBody`. `notifyBudgetExceeded` matches on the budget gate's string detail, which both
  // readers return identically, so it is unaffected by the split.
  const code = codeFromBody(body);
  notifyBudgetExceeded(code);

  return new ApiError(
    res.status,
    friendlyHttpMessage({ status: res.status, body }),
    code,
    body ?? text,
    `${context} ${res.status}${text ? `: ${text.slice(0, 500)}` : ''}`,
  );
}
